import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { SHOULD_PROPAGATE_UP } from '../../hooks/useGlobalBlockActions/const';
import type { OnActionArgs, OnActionType } from './context';
import { ActionsProviderContext, useActionsContext } from './context';

export default function ActionsProvider({
  children,
  onAction,
  isInstanceScopeBoundary = false,
}: {
  children: ReactNode;
  onAction: OnActionType;
  /**
   * Marks this provider as a page-instance/store-manager boundary (set by
   * InterfaceActionProvider, which is 1:1 with an InterfacePageProviders /
   * InterfaceStoreManager). An `instanceId` only addresses a store inside the
   * manager that registered it; the parent above this boundary is always a
   * DIFFERENT manager (a parent page) or the app level, where this id is
   * meaningless and would throw "store not found". So when an unhandled action
   * propagates past this boundary, clear instanceId — the parent resolves it in
   * its own scope (e.g. an app-level page variable handled by the app provider)
   * and keeps propagating, instead of trying to look up a foreign instance.
   */
  isInstanceScopeBoundary?: boolean;
}) {
  const { doAction: parentDoAction } = useActionsContext();
  const contextValue = useMemo(() => {
    return {
      doAction: async ({
        action,
        domEvent,
        actionContext,
        blockId,
        anchorId,
        instanceId,
      }: OnActionArgs) => {
        const shouldPropagate = await onAction({
          action,
          domEvent,
          actionContext,
          blockId,
          anchorId,
          instanceId,
        });

        // When shouldPropagate is false, we don't want to propagate the action to the parent
        if (shouldPropagate === SHOULD_PROPAGATE_UP) {
          await parentDoAction?.({
            action,
            domEvent,
            actionContext,
            blockId,
            anchorId,
            instanceId: isInstanceScopeBoundary ? undefined : instanceId,
          });
        }

        return SHOULD_PROPAGATE_UP;
      },
    };
  }, [onAction, parentDoAction, isInstanceScopeBoundary]);

  return (
    <ActionsProviderContext.Provider value={contextValue}>
      {children}
    </ActionsProviderContext.Provider>
  );
}
